import { api, publicApi } from '../lib/api'
import { resolveMediaUrl } from '../lib/mediaUrl'
import type { PeopleFilters } from '../types/people'
import { serializePeopleFilterParams } from '../types/people'
import { clearFlow, startFlow } from '../telemetry/flow'
import { getClientRequestId } from '../telemetry/request'

export type PeopleCard = {
  displayName: string
  handle: string
  avatar: string
  coverImage?: string
  motto?: string
  verified?: boolean
  public_id: string
  shareUrl?: string
  pro_title?: string
  pro_rate?: number | null
  pro_available_now?: boolean
  pro_rating?: number
  pro_completed_jobs_count?: number
  pros_to_go_enabled?: boolean
  home_zipcode?: string
  service_radius_miles?: number
  pro_mode?: 'REMOTE' | 'LOCAL' | 'BOTH'
}

type RequestOpts = { signal?: AbortSignal }

function normalizePeopleCard(raw: any): PeopleCard | null {
  if (!raw || typeof raw !== 'object') return null
  const publicId = raw.public_id ?? raw.publicId ?? null
  if (!publicId) return null
  const handleRaw = raw.handle ?? ''
  const handle = typeof handleRaw === 'string' && handleRaw
    ? (handleRaw.startsWith('@') ? handleRaw : `@${handleRaw}`)
    : ''
  return {
    displayName: raw.displayName ?? raw.display_name ?? '',
    handle,
    avatar: resolveMediaUrl(raw.avatar ?? raw.avatar_url) ?? '',
    coverImage: resolveMediaUrl(raw.coverImage ?? raw.cover_image) ?? undefined,
    motto: raw.motto ?? undefined,
    verified: Boolean(raw.verified),
    public_id: String(publicId),
    shareUrl: raw.shareUrl ?? raw.share_url ?? undefined,
    pro_title: raw.pro_title ?? undefined,
    pro_rate: raw.pro_rate ?? null,
    pro_available_now: raw.pro_available_now ?? false,
    pro_rating: raw.pro_rating ?? 0,
    pro_completed_jobs_count: raw.pro_completed_jobs_count ?? 0,
    pros_to_go_enabled: raw.pros_to_go_enabled ?? false,
    home_zipcode: raw.home_zipcode ?? '',
    service_radius_miles: raw.service_radius_miles ?? 0,
    pro_mode: raw.pro_mode ?? 'REMOTE',
  }
}

export async function fetchPeopleFeatured(limit = 24, sort?: 'trending' | 'newest', opts?: RequestOpts): Promise<PeopleCard[]> {
  const params: Record<string, any> = { limit }
  if (sort) params.sort = sort
  const { data } = await publicApi.get<PeopleCard[]>(`/api/people/featured/`, { params, signal: opts?.signal })
  const rows = Array.isArray(data) ? data : []
  return rows.map(normalizePeopleCard).filter(Boolean) as PeopleCard[]
}

export async function fetchPeopleFeaturedFiltered(opts?: { limit?: number; offset?: number; filters?: PeopleFilters; signal?: AbortSignal }): Promise<PeopleCard[]> {
  const limit = opts?.limit ?? 24
  const offset = opts?.offset ?? 0
  const params: Record<string, any> = { limit, offset }
  const fparams = serializePeopleFilterParams(opts?.filters)
  Object.entries(fparams).forEach(([k, v]) => { if (v !== undefined && v !== null) params[k] = v })
  if (opts?.filters?.q && opts.filters.q.trim()) params.q = opts.filters.q.trim()
  const { data } = await publicApi.get<PeopleCard[]>(`/api/people/featured/`, { params, signal: opts?.signal })
  const rows = Array.isArray(data) ? data : []
  return rows.map(normalizePeopleCard).filter(Boolean) as PeopleCard[]
}

export async function searchPeople(q: string, opts?: RequestOpts): Promise<PeopleCard[]> {
  const { data } = await publicApi.get<PeopleCard[] | { people?: PeopleCard[] }>(`/api/people/search/`, { params: { q }, signal: opts?.signal })
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.people) ? (data as any).people : []
  return rows.map(normalizePeopleCard).filter(Boolean) as PeopleCard[]
}

export async function searchPeopleFiltered(q: string, filters?: PeopleFilters, opts?: RequestOpts): Promise<PeopleCard[]> {
  const params: Record<string, any> = { q }
  const fparams = serializePeopleFilterParams(filters as any)
  Object.entries(fparams).forEach(([k, v]) => { if (v !== undefined && v !== null) params[k] = v })
  const { data } = await publicApi.get<PeopleCard[] | { people?: PeopleCard[] }>(`/api/people/search/`, { params, signal: opts?.signal })
  const rows = Array.isArray(data) ? data : Array.isArray((data as any)?.people) ? (data as any).people : []
  return rows.map(normalizePeopleCard).filter(Boolean) as PeopleCard[]
}

// ---- Profile social actions (mirror mobile) ----
export type ProfileLikeResponse = { liked: boolean; likes: number }
export type ProfileSaveResponse = { saved: boolean; saves?: number }
export type ProfileShareResponse = { shares: number }
export type ProfileFollowResponse = { is_following: boolean; followers_count: number; following_count: number }
export type FollowListRow = {
  person_id: number
  public_id: string
  handle: string
  display_name: string
  avatar_url: string
  bio: string
  followers_count: number
  following_count: number
  is_followed_by_viewer: boolean
  is_following_viewer: boolean
  is_mutual: boolean
  is_creator: boolean
  is_subscribed: boolean
}
export type FollowListResponse = { results: FollowListRow[]; page: number; page_size: number; count: number }

export async function likeProfile(publicId: string): Promise<ProfileLikeResponse> {
  const { data } = await api.post<ProfileLikeResponse>(`/api/people/i/${publicId}/like/`)
  return data
}

export async function unlikeProfile(publicId: string): Promise<ProfileLikeResponse> {
  const { data } = await api.delete<ProfileLikeResponse>(`/api/people/i/${publicId}/like/`)
  return data
}

export async function saveProfile(publicId: string): Promise<ProfileSaveResponse> {
  const { data } = await api.post<ProfileSaveResponse>(`/api/people/i/${publicId}/save/`)
  return data
}

export async function unsaveProfile(publicId: string): Promise<ProfileSaveResponse> {
  const { data } = await api.delete<ProfileSaveResponse>(`/api/people/i/${publicId}/save/`)
  return data
}

export async function shareProfile(publicId: string): Promise<ProfileShareResponse> {
  const { data } = await api.post<ProfileShareResponse>(`/api/people/i/${publicId}/share/`)
  return data
}

export async function followProfile(publicId: string): Promise<ProfileFollowResponse> {
  const flow = startFlow('follow_create')
  const headers = {
    'X-Client-Action': 'follow_create',
    'X-Client-Flow-Id': flow.flowId,
    'X-Client-Request-Id': getClientRequestId(),
  }
  try {
    const { data } = await api.post<ProfileFollowResponse>(`/api/people/i/${publicId}/follow/`, {}, { headers })
    return data
  } finally {
    clearFlow(flow.flowId)
  }
}

export async function unfollowProfile(publicId: string): Promise<ProfileFollowResponse> {
  const flow = startFlow('follow_delete')
  const headers = {
    'X-Client-Action': 'follow_delete',
    'X-Client-Flow-Id': flow.flowId,
    'X-Client-Request-Id': getClientRequestId(),
  }
  try {
    const { data } = await api.delete<ProfileFollowResponse>(`/api/people/i/${publicId}/follow/`, { headers })
    return data
  } finally {
    clearFlow(flow.flowId)
  }
}

export async function getFollowers(handle: string, page = 1, pageSize = 25): Promise<FollowListResponse> {
  const { data } = await publicApi.get<FollowListResponse>(`/api/people/${encodeURIComponent(handle)}/followers/`, {
    params: { page, page_size: pageSize },
  })
  return {
    ...data,
    results: (data.results || []).map((row) => ({
      ...row,
      avatar_url: resolveMediaUrl(row.avatar_url) ?? '',
    })),
  }
}

export async function getFollowing(handle: string, page = 1, pageSize = 25): Promise<FollowListResponse> {
  const { data } = await publicApi.get<FollowListResponse>(`/api/people/${encodeURIComponent(handle)}/following/`, {
    params: { page, page_size: pageSize },
  })
  return {
    ...data,
    results: (data.results || []).map((row) => ({
      ...row,
      avatar_url: resolveMediaUrl(row.avatar_url) ?? '',
    })),
  }
}
